import {
  Box,
  Button,
  Grid,
  LinearProgress,
  TextField,
  Typography,
} from "@mui/material";
import React, { ChangeEvent, useState } from "react";
import { UserDocument } from "../../models/UserDocument";
import { documentsAPI } from "../../apis";
import { USER_DOC_UPLOAD_TYPES, MAX_UPLOAD_BYTES } from "../../constants";
import { Modes as DialogModes } from "./UserDocumentDialog";
import { DocumentThumbnail } from "./DocumentThumbnail";
import { Link } from "react-router-dom";
import { Loading } from "views/shared/Loading";
import { DocumentTypeFormControl } from "views/shared/forms/DocumentTypeFormControl";
import { ZoomIn } from "@mui/icons-material";

interface Props {
  document: UserDocument;
  documentSrc: string;
  setMode?: (mode: DialogModes) => void;
  onComplete?: () => void;
  onCancel?: () => void;
  onClickThumbnail?: () => void;
  onUpdateDoc?: () => void;
  includeOnlyDocumentTypeIds?: number[],
}

export const EditUserDocument = ({
  document,
  documentSrc,
  onComplete,
  onCancel,
  onUpdateDoc,
  onClickThumbnail,
  includeOnlyDocumentTypeIds,
}: Props): JSX.Element => {
  const isNew = !document?.id;
  const inputRef = React.useRef<HTMLInputElement>(null);
  const submitButtonRef = React.useRef<HTMLButtonElement>(null);

  const [title, setTitle] = useState(document?.version?.title || "");
  const [notes, setNotes] = useState(document?.notes || "");
  const [documentType, setDocumentType] = useState<number>(getInitialDocTypeId());
  const [uploadedFiles, setUploadedFiles] = useState<FileList | undefined>();
  const [progress, setProgress] = useState<number | undefined>();
  const [errors, setErrors] = useState<string[]>([]);
  const [successes, setSuccesses] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  function getInitialDocTypeId(): number {
    if (document.documentTypeId) {
      return document.documentTypeId
    }

    if (includeOnlyDocumentTypeIds && includeOnlyDocumentTypeIds.length > 0) {
      return includeOnlyDocumentTypeIds[0]
    }

    return 1 // "other"
  }

  // triggers when file is selected with click
  const handleChange = async function (e: ChangeEvent<HTMLInputElement>) {
    e.preventDefault();

    setErrors([]);
    if (e.target.files && e.target.files[0]) {
      await validateFile(e.target.files);
      //await handleSubmit(); // doesn't work for some reason... just click the submit button instead
      submitButtonRef?.current?.click();
    }
  };

  const validateFile = async (inputFiles: FileList): Promise<void> => {
    if (inputFiles.length === 0) {
      setErrors(["Please drop or select a file."]);
      return;
    }
    const errs: string[] = [];
    for (let i = 0; i < inputFiles.length; i++) {
      const file: File | null = inputFiles.item(i);
      if (file) {
        if (file.size > MAX_UPLOAD_BYTES) {
          console.error(file.size, ">", MAX_UPLOAD_BYTES);
          errs.push(`"${file.name}" is too large`);
        }
        if (!USER_DOC_UPLOAD_TYPES.includes(file.type)) {
          console.error(file.type, "!includes", USER_DOC_UPLOAD_TYPES);
          errs.push(`"${file.name}" file type: ${file.type} not accepted`);
        }
      } else {
        errs.push("no file");
      }
    }
    setUploadedFiles(inputFiles);
    if (errs?.length) {
      setErrors(errs);
    }
  };

  const handleSubmit = async (): Promise<void> => {
    setIsSubmitting(true);
    setProgress(undefined);
    setSuccesses([]);
    setErrors([]);
    const submitErrors: string[] = [];
    const submitSuccesses: string[] = [];
    if (!title?.trim()) {
      submitErrors.push("Please enter a document title.");
    }

    const uploadFile = uploadedFiles?.item(0) || undefined;
    if (isNew) {
      // if they selected/dragged a file it will be in upload file
      if (!uploadFile) {
        submitErrors.push("Please choose a document to upload.");
      }
    }
    if (submitErrors?.length) {
      setErrors(submitErrors);
      setIsSubmitting(false);
      return;
    }

    const result = await documentsAPI.saveDocument(
      title,
      document?.id,
      documentType, //id
      uploadFile, // will be undefined if only the title is being editted
      notes,
      undefined,
      (percent) => setProgress(percent), // callback to display progress,
      false
    );
    setProgress(undefined);
    setUploadedFiles(undefined);
    setIsSubmitting(false);
    if (result) {
      submitSuccesses.push("File updated successfully");
      setSuccesses(submitSuccesses);
      if (uploadFile && onUpdateDoc) {
        onUpdateDoc();
      } else if (!uploadFile && onComplete) {
        onComplete();
      }
      return;
    } else {
      setErrors(["Sorry, file upload failed.  Please try again."]);
    }
  };

  return (
    <Grid container spacing={2} style={{ width: "100%", minWidth: "400px" }}>
      <Grid item xs={12} sm={12} md={5} lg={5} xl={5}>
        <Link
          to={""}
          style={{
            display: "flex",
            padding: "1rem",
            alignItems: "center",
            justifyContent: "center",
            alignContent: "center",
          }}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onClickThumbnail?.();
          }}
        >
          <DocumentThumbnail
            document={document}
            documentSrc={documentSrc}
            showZoomIcon={true}
            style={
              document?.version?.ext === "pdf" ? { minHeight: "300px" } : {}
            }
          />
        </Link>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          <input
            type="file"
            ref={inputRef}
            id="input-file-upload"
            style={{ display: "none" }}
            onChange={handleChange}
          />
          <Button
            style={{ display: "none" }}
            ref={submitButtonRef}
            type="submit"
          />
          <label
            id="label-file-upload"
            htmlFor="input-file-upload"
            style={{
              display: "flex",
              cursor: "pointer",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography variant="body1" color="secondary">
              Upload or update file
            </Typography>
          </label>
        </form>
      </Grid>
      <Grid item xs={12} sm={12} md={6} lg={7} xl={7}>
        <TextField
          fullWidth
          variant="outlined"
          name="title"
          label="Document Title"
          value={title}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            setTitle(event.target.value);
          }}
          onFocus={() => setErrors([])}
          style={{ marginBottom: "1rem", marginTop: "1rem" }}
        />
        <DocumentTypeFormControl
          initialDocumentTypeId={documentType}
          onFocus={() => setErrors([])}
          onChange={(documentTypeId: number) => {
            setDocumentType(documentTypeId)
          }}
          includeOnlyOptions={includeOnlyDocumentTypeIds}
        />
        <TextField
          variant="outlined"
          name="notes"
          label="Notes"
          style={{ width: "100%" }}
          minRows={3}
          maxRows={10}
          inputProps={{ maxLength: 5000 }}
          value={notes}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            setNotes(event.target.value);
          }}
          onFocus={() => setErrors([])}
          multiline={true}
        />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            "& button": {
              mt: 1,
            },
          }}
        >
          <Grid container spacing={1}>
            <Grid item xs={7} sm={7} md={7} lg={7} xl={7}>
              <Button
                color="secondary"
                variant="contained"
                autoFocus
                disabled={isSubmitting}
                style={{ width: "100%" }}
                onClick={handleSubmit}
              >
                {isNew ? "Upload" : "Update Details"}
              </Button>
              {/* <Button
                      color="secondary"
                      variant="outlined"
                      size="small"
                      style={{width:"100%"}}
                      onClick={() => setMode?.(DialogModes.versions)}
                    >
                    Version History
                  </Button>     */}
            </Grid>
            <Grid item xs={5} sm={5} md={5} lg={5} xl={5}>
              <Button
                onClick={() => (onCancel ? onCancel() : null)}
                variant={"outlined"}
                color="secondary"
                style={{ width: "100%" }}
              >
                Cancel
              </Button>
            </Grid>
          </Grid>
          <div style={{ height: "30px", overflow: "hidden" }}>
            {progress && (
              <Grid container style={{ marginTop: "5px" }}>
                <Grid item xs={3} sm={3} md={3} lg={3} xl={3}>
                  <Loading />
                </Grid>
                <Grid item xs={9} sm={9} md={9} lg={9} xl={9}>
                  <LinearProgress
                    variant="determinate"
                    value={progress}
                    color="secondary"
                    sx={{
                      height: 16,
                      mt: 2,
                      mb: 1,
                    }}
                  />
                </Grid>
              </Grid>
            )}
            {errors && (
              <Typography
                component={"p"}
                color="error"
                sx={{ width: "100%", paddingTop: "5px", textAlign: "center" }}
              >
                {errors.join(", ")}
              </Typography>
            )}
            <Typography
              color="success.dark"
              variant={"body1"}
              style={{ marginTop: "10px" }}
            >
              {successes.join(", ")}
            </Typography>
          </div>
        </Box>
      </Grid>
    </Grid>
  );
};
